import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  TextField,
  Paper,
  Divider,
  Alert,
  CircularProgress,
  Switch,
  FormControlLabel,
  InputAdornment,
  Chip,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Casino as CasinoIcon,
  TrendingUp as TrendingUpIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Timer as TimerIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { sevenUpDownAPI } from '../../../services/api';

const SevenUpDownSettings = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    betting_duration: 15,
    rolling_duration: 5,
    result_display_duration: 5,
    min_bet_amount: 10.00,
    max_bet_amount: 10000.00,
    multiplier_down: 2.00,
    multiplier_seven: 5.00,
    multiplier_up: 2.00,
    is_game_active: true,
    maintenance_mode: false,
    maintenance_message: '',
    house_edge_target: 0.05,
    max_win_per_round: 100000.00,
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await sevenUpDownAPI.getSettings();
      if (response.data.success) {
        const settingsData = response.data.settings;
        setSettings(settingsData);
        setFormData({
          betting_duration: settingsData.betting_duration || 15,
          rolling_duration: settingsData.rolling_duration || 5,
          result_display_duration: settingsData.result_display_duration || 5,
          min_bet_amount: parseFloat(settingsData.min_bet_amount) || 10.00,
          max_bet_amount: parseFloat(settingsData.max_bet_amount) || 10000.00,
          multiplier_down: settingsData.multipliers?.DOWN || parseFloat(settingsData.multiplier_down) || 2.00,
          multiplier_seven: settingsData.multipliers?.SEVEN || parseFloat(settingsData.multiplier_seven) || 5.00,
          multiplier_up: settingsData.multipliers?.UP || parseFloat(settingsData.multiplier_up) || 2.00,
          is_game_active: settingsData.is_game_active !== false,
          maintenance_mode: settingsData.maintenance_mode || false,
          maintenance_message: settingsData.maintenance_message || '',
          house_edge_target: parseFloat(settingsData.house_edge_target) || 0.05,
          max_win_per_round: parseFloat(settingsData.max_win_per_round) || 100000.00,
        });
      }
    } catch (err) {
      console.error('Error fetching settings:', err);
      toast.error(err.response?.data?.message || 'Failed to fetch settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (formData.min_bet_amount >= formData.max_bet_amount) {
      toast.error('Minimum bet must be less than maximum bet');
      return;
    }
    
    if (formData.multiplier_down <= 1 || formData.multiplier_seven <= 1 || formData.multiplier_up <= 1) {
      toast.error('All multipliers must be greater than 1');
      return;
    }

    try {
      setSaving(true);
      const response = await sevenUpDownAPI.updateSettings(formData);
      
      if (response.data.success) {
        toast.success('Settings updated successfully');
        fetchSettings();
      } else {
        toast.error(response.data.message || 'Failed to update settings');
      }
    } catch (err) {
      console.error('Error updating settings:', err);
      toast.error(err.response?.data?.message || 'Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleGameActive = async () => {
    try {
      setSaving(true);
      const newValue = !formData.is_game_active;
      const response = await sevenUpDownAPI.updateSettings({ is_game_active: newValue });
      
      if (response.data.success) {
        toast.success(`Game ${newValue ? 'activated' : 'deactivated'}`);
        setFormData({ ...formData, is_game_active: newValue });
        fetchSettings();
      }
    } catch (err) {
      console.error('Error toggling game status:', err);
      toast.error(err.response?.data?.message || 'Failed to toggle game status');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleMaintenance = async () => {
    try {
      setSaving(true);
      const newValue = !formData.maintenance_mode;
      const response = await sevenUpDownAPI.updateSettings({ maintenance_mode: newValue });
      
      if (response.data.success) {
        toast.success(`Maintenance mode ${newValue ? 'enabled' : 'disabled'}`);
        setFormData({ ...formData, maintenance_mode: newValue });
        fetchSettings();
      }
    } catch (err) {
      console.error('Error toggling maintenance:', err);
      toast.error(err.response?.data?.message || 'Failed to toggle maintenance mode');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (settings) {
      setFormData({
        betting_duration: settings.betting_duration || 15,
        rolling_duration: settings.rolling_duration || 5,
        result_display_duration: settings.result_display_duration || 5,
        min_bet_amount: parseFloat(settings.min_bet_amount) || 10.00,
        max_bet_amount: parseFloat(settings.max_bet_amount) || 10000.00,
        multiplier_down: settings.multipliers?.DOWN || parseFloat(settings.multiplier_down) || 2.00,
        multiplier_seven: settings.multipliers?.SEVEN || parseFloat(settings.multiplier_seven) || 5.00,
        multiplier_up: settings.multipliers?.UP || parseFloat(settings.multiplier_up) || 2.00,
        is_game_active: settings.is_game_active !== false,
        maintenance_mode: settings.maintenance_mode || false,
        maintenance_message: settings.maintenance_message || '',
        house_edge_target: parseFloat(settings.house_edge_target) || 0.05,
        max_win_per_round: parseFloat(settings.max_win_per_round) || 100000.00,
      });
      toast.info('Form reset to current saved settings');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Box sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
            <SettingsIcon sx={{ mr: 2 }} />
            🎲 7 Up Down Settings
          </Typography>
          <Box display="flex" gap={2}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={handleReset}
              disabled={saving}
            >
              Reset
            </Button>
          </Box>
        </Box>

        {/* Game Status */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">Game Status</Typography>
              <Box display="flex" gap={2}>
                <Chip
                  label={formData.is_game_active ? 'ACTIVE' : 'INACTIVE'}
                  color={formData.is_game_active ? 'success' : 'error'}
                  icon={formData.is_game_active ? <CheckCircleIcon /> : <WarningIcon />}
                />
                {formData.maintenance_mode && (
                  <Chip
                    label="MAINTENANCE"
                    color="warning"
                    icon={<WarningIcon />}
                  />
                )}
              </Box>
            </Box>

            {(formData.maintenance_mode || !formData.is_game_active) && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {formData.maintenance_mode 
                  ? 'Game is in maintenance mode. Players cannot place bets.'
                  : 'Game is inactive. Players cannot play.'}
              </Alert>
            )}

            <Box display="flex" gap={2}>
              <Button
                variant={formData.is_game_active ? 'contained' : 'outlined'}
                color="success"
                onClick={handleToggleGameActive}
                disabled={saving || formData.is_game_active}
              >
                Activate Game
              </Button>
              <Button
                variant={!formData.is_game_active ? 'contained' : 'outlined'}
                color="error"
                onClick={handleToggleGameActive}
                disabled={saving || !formData.is_game_active}
              >
                Deactivate Game
              </Button>
              <Button
                variant={formData.maintenance_mode ? 'contained' : 'outlined'}
                color="warning"
                onClick={handleToggleMaintenance}
                disabled={saving}
              >
                {formData.maintenance_mode ? 'Disable Maintenance' : 'Enable Maintenance'}
              </Button>
            </Box>
          </CardContent>
        </Card>

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Game Timing */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                    <TimerIcon sx={{ mr: 1 }} />
                    Game Timing
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Betting Duration"
                        type="number"
                        value={formData.betting_duration}
                        onChange={(e) => setFormData({ ...formData, betting_duration: parseInt(e.target.value) })}
                        required
                        inputProps={{ min: 5, max: 60 }}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">seconds</InputAdornment>,
                        }}
                        helperText="Time players have to place bets (5-60 seconds)"
                      />
                    </Grid>
                    
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Rolling Duration"
                        type="number"
                        value={formData.rolling_duration}
                        onChange={(e) => setFormData({ ...formData, rolling_duration: parseInt(e.target.value) })}
                        required
                        inputProps={{ min: 2, max: 15 }}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">seconds</InputAdornment>,
                        }}
                        helperText="Dice rolling animation time"
                      />
                    </Grid>
                    
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Result Display Duration"
                        type="number"
                        value={formData.result_display_duration}
                        onChange={(e) => setFormData({ ...formData, result_display_duration: parseInt(e.target.value) })}
                        required
                        inputProps={{ min: 2, max: 15 }}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">seconds</InputAdornment>,
                        }}
                        helperText="Time to display results"
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Betting Limits */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                    <TrendingUpIcon sx={{ mr: 1 }} />
                    Betting Limits
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Minimum Bet Amount"
                        type="number"
                        value={formData.min_bet_amount}
                        onChange={(e) => setFormData({ ...formData, min_bet_amount: parseFloat(e.target.value) })}
                        required
                        inputProps={{ step: 0.01, min: 1 }}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                        }}
                        helperText="Minimum bet per round"
                      />
                    </Grid>
                    
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Maximum Bet Amount"
                        type="number"
                        value={formData.max_bet_amount}
                        onChange={(e) => setFormData({ ...formData, max_bet_amount: parseFloat(e.target.value) })}
                        required
                        inputProps={{ step: 0.01, min: 10 }}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                        }}
                        helperText="Maximum bet per round"
                      />
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Max Win Per Round"
                        type="number"
                        value={formData.max_win_per_round}
                        onChange={(e) => setFormData({ ...formData, max_win_per_round: parseFloat(e.target.value) })}
                        required
                        inputProps={{ step: 100, min: 1000 }}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                        }}
                        helperText="Maximum payout per round"
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Multipliers */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                    <CasinoIcon sx={{ mr: 1 }} />
                    Betting Multipliers
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  
                  <Alert severity="info" sx={{ mb: 3 }}>
                    <strong>7 Up Down Rules:</strong><br />
                    • DOWN (2-6): Sum is less than 7<br />
                    • SEVEN (7): Sum equals exactly 7<br />
                    • UP (8-12): Sum is greater than 7
                  </Alert>
                  
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                      <Paper sx={{ p: 2, textAlign: 'center', background: 'linear-gradient(135deg, #f4433620 0%, #f4433610 100%)' }}>
                        <Typography variant="h6" color="#f44336" gutterBottom>
                          ⬇️ DOWN (2-6)
                        </Typography>
                        <TextField
                          fullWidth
                          label="Multiplier"
                          type="number"
                          value={formData.multiplier_down}
                          onChange={(e) => setFormData({ ...formData, multiplier_down: parseFloat(e.target.value) })}
                          required
                          inputProps={{ step: 0.01, min: 1.01 }}
                          InputProps={{
                            endAdornment: <InputAdornment position="end">x</InputAdornment>,
                          }}
                          helperText="Probability: ~41.67%"
                        />
                      </Paper>
                    </Grid>
                    
                    <Grid item xs={12} md={4}>
                      <Paper sx={{ p: 2, textAlign: 'center', background: 'linear-gradient(135deg, #9c27b020 0%, #9c27b010 100%)' }}>
                        <Typography variant="h6" color="#9c27b0" gutterBottom>
                          7️⃣ SEVEN (7)
                        </Typography>
                        <TextField
                          fullWidth
                          label="Multiplier"
                          type="number"
                          value={formData.multiplier_seven}
                          onChange={(e) => setFormData({ ...formData, multiplier_seven: parseFloat(e.target.value) })}
                          required
                          inputProps={{ step: 0.01, min: 1.01 }}
                          InputProps={{
                            endAdornment: <InputAdornment position="end">x</InputAdornment>,
                          }}
                          helperText="Probability: ~16.67%"
                        />
                      </Paper>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <Paper sx={{ p: 2, textAlign: 'center', background: 'linear-gradient(135deg, #4caf5020 0%, #4caf5010 100%)' }}>
                        <Typography variant="h6" color="#4caf50" gutterBottom>
                          ⬆️ UP (8-12)
                        </Typography>
                        <TextField
                          fullWidth
                          label="Multiplier"
                          type="number"
                          value={formData.multiplier_up}
                          onChange={(e) => setFormData({ ...formData, multiplier_up: parseFloat(e.target.value) })}
                          required
                          inputProps={{ step: 0.01, min: 1.01 }}
                          InputProps={{
                            endAdornment: <InputAdornment position="end">x</InputAdornment>,
                          }}
                          helperText="Probability: ~41.67%"
                        />
                      </Paper>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* House Edge */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    🏠 House Edge
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  
                  <TextField
                    fullWidth
                    label="House Edge Target"
                    type="number"
                    value={formData.house_edge_target}
                    onChange={(e) => setFormData({ ...formData, house_edge_target: parseFloat(e.target.value) })}
                    required
                    inputProps={{ step: 0.01, min: 0, max: 1 }}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">%</InputAdornment>,
                    }}
                    helperText="Target house edge (0.05 = 5%)"
                  />
                </CardContent>
              </Card>
            </Grid>

            {/* Maintenance Message */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    📢 Maintenance Message
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Maintenance Message"
                    value={formData.maintenance_message}
                    onChange={(e) => setFormData({ ...formData, maintenance_message: e.target.value })}
                    placeholder="Enter message to show users during maintenance..."
                    helperText="This message will be displayed to users when maintenance mode is active"
                  />
                </CardContent>
              </Card>
            </Grid>

            {/* Save Button */}
            <Grid item xs={12}>
              <Box display="flex" justifyContent="flex-end" gap={2}>
                <Button
                  variant="outlined"
                  onClick={handleReset}
                  disabled={saving}
                >
                  Reset Changes
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  size="large"
                  startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save Settings'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Box>
    </motion.div>
  );
};

export default SevenUpDownSettings;
